import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { Checkbox } from "components/common/checkbox";
import { BudgetCategory } from "../types";
import { TARGETED_RERUN_KEEP_NOTE, targetedRerunActionLabel } from "../targetedRerun";

export interface ChooseCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Only the categories AI can regenerate. */
  categories: BudgetCategory[];
  selectedCategoryIds: number[];
  onToggleCategory: (categoryId: number) => void;
  onSubmit: () => void;
  /** A run is active or a request is out: the action is unavailable. */
  isPending: boolean;
  /** This dialog's request is out: the action shows progress and the choice is locked. */
  isSubmitting: boolean;
  rerunParamsNotice?: string | null;
  costLine: string | null;
  refusal?: string | null;
  /** The control that opened the dialog, which gets focus back on close. */
  returnFocusTo: () => HTMLElement | null | undefined;
}

export const ChooseCategoriesDialog = ({
  open,
  onClose,
  categories,
  selectedCategoryIds,
  onToggleCategory,
  onSubmit,
  isPending,
  isSubmitting,
  rerunParamsNotice,
  costLine,
  refusal,
  returnFocusTo,
}: ChooseCategoriesDialogProps) => {
  const groupLabelId = useId();
  const refusalId = useId();
  // State, not a ref: the dialog's content mounts a render after it opens, and the refusal focus
  // below must run again once the picks exist.
  const [groupNode, setGroupNode] = useState<HTMLDivElement | null>(null);
  const focusedRefusalRef = useRef<string | null>(null);
  // Only picks still in the list count; an id from an older version of the budget is ignored.
  const selectedIds = useMemo(() => {
    const listed = new Set(categories.map((category) => category.budget_category_id));
    return selectedCategoryIds.filter((id) => listed.has(id));
  }, [categories, selectedCategoryIds]);
  const selectedCount = selectedIds.length;

  // Nothing closes the dialog while the request is out, so its answer always has a place to show.
  const requestClose = () => {
    if (!isSubmitting) onClose();
  };

  // A refusal moves focus to the picks it is about, which it describes, so they can be changed.
  useEffect(() => {
    if (!refusal) {
      focusedRefusalRef.current = null;
      return;
    }
    if (!open || isSubmitting || !groupNode || focusedRefusalRef.current === refusal) return;
    const boxes = Array.from(
      groupNode.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
    );
    const target = boxes.find((box) => box.checked) ?? boxes[0];
    if (!target) return;
    focusedRefusalRef.current = refusal;
    target.focus();
  }, [open, refusal, isSubmitting, groupNode]);

  const handleSubmit = () => {
    // Busy, not disabled, while the request is out: a disabled button would drop focus to the page.
    if (isSubmitting) return;
    onSubmit();
  };

  return (
    <Modal
      layout="panel"
      isOpen={open}
      onClose={requestClose}
      maxWidth="max-w-md"
      getReturnFocusTarget={returnFocusTo}
    >
      <ModalHeader
        layout="panel"
        title="Choose what AI updates"
        onClose={requestClose}
        closeDisabled={isSubmitting}
      />
      <ModalBody layout="panel" showBorders={false}>
        <div className="flex flex-col gap-4">
          {rerunParamsNotice ? (
            <p className="m-0 text-xs text-muted-foreground" role="note">
              {rerunParamsNotice}
            </p>
          ) : null}
          <span id={groupLabelId} className="sr-only">
            Categories AI updates
          </span>
          <div
            ref={setGroupNode}
            role="group"
            aria-labelledby={groupLabelId}
            aria-describedby={refusal ? refusalId : undefined}
            className="flex flex-col gap-1"
          >
            {categories.map((category) => (
              <label
                key={category.budget_category_id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted/60"
              >
                <Checkbox
                  name={`targeted-category-${category.budget_category_id}`}
                  checked={selectedIds.includes(category.budget_category_id)}
                  disabled={isSubmitting}
                  onChange={() => onToggleCategory(category.budget_category_id)}
                />
                <span className="capitalize">{category.name}</span>
              </label>
            ))}
          </div>
          {selectedCount > 0 || costLine ? (
            <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
              {selectedCount > 0 ? <p className="m-0">{TARGETED_RERUN_KEEP_NOTE}</p> : null}
              {costLine ? <p className="m-0">{costLine}</p> : null}
            </div>
          ) : null}
          {refusal ? (
            <p id={refusalId} className="m-0 text-xs text-danger" role="alert">
              {refusal}
            </p>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter layout="panel">
        <div className="flex w-full justify-end">
          <Button
            type="button"
            size="sm"
            variant="badge"
            color="brand"
            disabled={selectedCount === 0 || (isPending && !isSubmitting)}
            aria-disabled={isSubmitting || undefined}
            aria-busy={isSubmitting || undefined}
            className={isSubmitting ? "cursor-wait opacity-70 hover:scale-100" : undefined}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" aria-hidden data-testid="button-spinner" />
            ) : null}
            {targetedRerunActionLabel(selectedCount)}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
